/* eslint-disable react/prop-types */
import React, { memo, useContext, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from '@agconnections/grow-ui';
import { noop } from 'lodash';

import FormattedNumber from 'components/FormattedNumber';

import ClickableDiv from 'components/ClickableDiv';
import SelectedPropertyContext from '../SelectedPropertyContext';
import { calculateFarmAcres } from '../../helpers/leftPanel';
import EditRowButton from '../EditRowButton';
import UnarchiveButton from '../UnarchiveButton';

const FarmRow = ({
  farm,
  properties,
  onSelect,
  hasEditAccess,
  showUnarchiveButton,
  onUnarchiveProperty
}) => {
  const { selectedPropertyId, dispatch } = useContext(SelectedPropertyContext);
  const isSelected = selectedPropertyId === farm.id;

  const totalAcres = useMemo(() => calculateFarmAcres(farm.fields), [
    farm.fields
  ]);
  const [showEditButton, setShowEditButton] = useState(false);
  const [isOnHover, setIsOnHover] = useState(false);

  const updateFarm = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      dispatch({
        type: 'SELECT_PROPERTY',
        payload: {
          ...farm,
          isEditing: true,
          type: 'Farm'
        }
      });
    },
    [dispatch, farm]
  );

  const handleMouseEnter = useCallback(() => {
    setShowEditButton(true);
    setIsOnHover(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowEditButton(false);
    setIsOnHover(false);
  }, []);

  const handleFieldCount = () => {
    const farmName = farm.name;
    const _farm = properties.find(property => property.name === farmName);
    if (_farm) {
      return _farm.fields.length;
    }
    return 0;
  };

  const displayByCropData = () => {
    if (properties) {
      return `${farm.fields.length} of ${handleFieldCount()}`;
    }
    return '';
  };

  const displayFieldData = () => {
    if (properties) {
      return `field${handleFieldCount() <= 1 ? '' : 's'}`;
    }
    if (farm.archived) {
      return 'Archived';
    }
    if (!farm.fields.length) {
      return 'No fields';
    }
    return `${farm.fields.length} field${farm.fields.length === 1 ? '' : 's'}`;
  };

  const handleUnarchive = useCallback(() => onUnarchiveProperty(farm), [
    onUnarchiveProperty,
    farm
  ]);

  return (
    <ClickableDiv
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => onSelect(farm)}
      className="w-full py-3 pl-8 pr-1 text-left flex justify-between items-center"
    >
      {isSelected && (
        <div className="absolute left-0 h-50px w-1 bg-blue-50 rounded-r" />
      )}
      <div data-testid={`farm-row-${farm.id}`}>
        <p
          className={`font-medium leading-5 ${
            farm.status && !farm.archived ? '' : 'text-neutral-40'
          } ${isSelected ? 'font-semibold' : ''}`}
        >
          {farm.name}
        </p>
        {farm.status ? (
          <div className="mt-2px text-xs leading-3">
            <span className={`text-neutral-${!farm.archived ? '60' : '40'}`}>
              <FormattedNumber roundedTo={2}>{totalAcres}</FormattedNumber> ac
              <span> • </span>
              {displayByCropData()} {displayFieldData()}
            </span>
          </div>
        ) : (
          <div className="flex mt-2">
            <span className="mr-2" style={{ height: '14px' }}>
              <Spinner size="xs" color="#696F88" />
            </span>
            <span className="text-gray-700">Loading...</span>
          </div>
        )}
      </div>
      {hasEditAccess && showEditButton && (
        <EditRowButton isRowSelected={isSelected} onClick={updateFarm} />
      )}
      {showUnarchiveButton && (
        <UnarchiveButton onUnarchive={handleUnarchive} isOnHover={isOnHover} />
      )}
    </ClickableDiv>
  );
};

FarmRow.defaultProps = {
  farm: {
    id: '',
    name: '',
    fields: [],
    status: null
  },
  onSelect: noop,
  showUnarchiveButton: false,
  onUnarchiveProperty: noop,
  properties: undefined,
  hasEditAccess: false
};

FarmRow.propTypes = {
  farm: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.object),
    status: PropTypes.number,
    archived: PropTypes.bool,
    parentPage: PropTypes.number
  }),
  showUnarchiveButton: PropTypes.bool,
  onUnarchiveProperty: PropTypes.func,
  onSelect: PropTypes.func,
  hasEditAccess: PropTypes.bool,
  properties: PropTypes.objectOf(PropTypes.object)
};

export default memo(FarmRow);
